import type { ChatExperienceConfig, PromptSuggestion } from '../../../types/wizardTypes';

/**
 * Convert visual chat experience config to annotations JSON object.
 */
export function configToAnnotations(config: ChatExperienceConfig): object {
  const annotations: Record<string, unknown> = {};

  const validSuggestions = config.promptSuggestions.filter(s => s.text.trim() !== '');
  if (validSuggestions.length > 0) {
    annotations.promptSuggestion = validSuggestions.map(s => ({
      text: s.text,
      requiredFields: s.requiredFields,
      labels: s.labels,
      enabled: s.enabled ?? true,
    }));
  }

  if (Object.keys(config.missingFieldQuestions).length > 0) {
    const filtered: Record<string, string> = {};
    for (const [field, question] of Object.entries(config.missingFieldQuestions)) {
      if (question.trim()) filtered[field] = question;
    }
    if (Object.keys(filtered).length > 0) {
      annotations.missingFieldQuestions = filtered;
    }
  }

  if (config.confirmBeforeExecute) {
    annotations.requiresConfirmation = true;
  }

  return annotations;
}

/**
 * Convert annotations JSON back to chat experience config (for edit-mode hydration).
 */
export function annotationsToConfig(annotations: unknown): ChatExperienceConfig {
  const config: ChatExperienceConfig = {
    promptSuggestions: [],
    missingFieldQuestions: {},
    confirmBeforeExecute: false,
  };

  if (!annotations || typeof annotations !== 'object') return config;

  const a = annotations as Record<string, unknown>;

  if (Array.isArray(a.promptSuggestion)) {
    config.promptSuggestions = a.promptSuggestion
      .filter((s): s is Record<string, unknown> => s && typeof s === 'object')
      .map(s => ({
        text: typeof s.text === 'string' ? s.text : '',
        requiredFields: Array.isArray(s.requiredFields)
          ? s.requiredFields.filter((f): f is string => typeof f === 'string')
          : [],
        labels: s.labels && typeof s.labels === 'object'
          ? { ...(s.labels as Record<string, string>) }
          : {},
        enabled: typeof s.enabled === 'boolean' ? s.enabled : true,
      }));
  }
  // Legacy format: simple prompts string array
  else if (Array.isArray(a.prompts)) {
    config.promptSuggestions = a.prompts
      .filter((p): p is string => typeof p === 'string')
      .map(text => ({ text, requiredFields: [], labels: {} }));
  }

  if (a.missingFieldQuestions && typeof a.missingFieldQuestions === 'object') {
    config.missingFieldQuestions = { ...(a.missingFieldQuestions as Record<string, string>) };
  }

  if (a.requiresConfirmation === true) {
    config.confirmBeforeExecute = true;
  }

  return config;
}

export function getDefaultChatConfig(): ChatExperienceConfig {
  return {
    promptSuggestions: [{ text: '', requiredFields: [], labels: {} }],
    missingFieldQuestions: {},
    confirmBeforeExecute: false,
  };
}

export function createEmptySuggestion(): PromptSuggestion {
  return { text: '', requiredFields: [], labels: {} };
}
